import { QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { createQueryClient } from '@/api/queryClient'
import { RootErrorBoundary } from '@/components/RootErrorBoundary'
import { LoadingState, Toaster } from '@/components/ui'
import { AppShell } from '@/layouts/AppShell'
import { AuthProvider } from '@/stores/AuthProvider'
import { ThemeProvider } from '@/stores/ThemeProvider'

/**
 * 应用装配。
 *
 * Provider 的嵌套顺序不是随意的：
 *   ErrorBoundary → Theme → QueryClient → Router → Auth
 *
 *   - ErrorBoundary 最外：它要能兜住任何一层的渲染异常；
 *   - Theme 在 QueryClient 之外：Toaster 需要读主题，而它渲染在 Router 之外；
 *   - Auth 在 Router 之内：登录成功后要 `navigate`，得先有路由上下文；
 *     它也在 QueryClient 之内，因为将来登出时要清缓存。
 *
 * 首页与详情页直接打包进主 chunk（它们是绝大多数访问的落地页）；发布页、下载页、
 * 登录注册这些低频且体积偏大的（Tiptap 就有几百 KB）走 lazy，避免让首屏为
 * 一个大多数人不会打开的编辑器付费。
 */

const SearchPage = lazy(() =>
  import('@/features/search/SearchPage').then((module) => ({ default: module.SearchPage })),
)
const DownloadPage = lazy(() =>
  import('@/features/download/DownloadPage').then((module) => ({ default: module.DownloadPage })),
)
const PublishPage = lazy(() =>
  import('@/features/publish/PublishPage').then((module) => ({ default: module.PublishPage })),
)
const LoginPage = lazy(() =>
  import('@/features/auth/AuthPages').then((module) => ({ default: module.LoginPage })),
)
const RegisterPage = lazy(() =>
  import('@/features/auth/AuthPages').then((module) => ({ default: module.RegisterPage })),
)
const FavoritesPage = lazy(() =>
  import('@/features/favorites/FavoritesPage').then((module) => ({
    default: module.FavoritesPage,
  })),
)
const AccountPage = lazy(() =>
  import('@/features/settings/AccountPage').then((module) => ({ default: module.AccountPage })),
)

/*
  邮件链接的落地页。

  前三个的**路径是契约**：后端用 `MODSITE_FRONTEND_BASE_URL` 把它们拼进邮件正文
  （`/verify-email`、`/reset-password`、`/confirm-email`），改路径等于让已经发出去
  的邮件全部失效。`/forgot-password` 是前端自己定的。

  四个共享一个模块、一次 lazy：访问频率极低（一个用户一辈子点几次），
  没理由让首屏为它们付费，而它们之间共用的提示块也不必拆成四个 chunk。
*/
const VerifyEmailPage = lazy(() =>
  import('@/features/auth/MailFlowPages').then((module) => ({ default: module.VerifyEmailPage })),
)
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/MailFlowPages').then((module) => ({ default: module.ResetPasswordPage })),
)
const ConfirmEmailPage = lazy(() =>
  import('@/features/auth/MailFlowPages').then((module) => ({ default: module.ConfirmEmailPage })),
)
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/MailFlowPages').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
)

/*
  管理后台。

  整棵子树都走 lazy，而且**刻意不做 prefetch**：绝大多数访问者永远不会打开它，
  没有理由让首屏为一个管理控制台付费。它也不共享 `AppShell` —— 管理端有自己的
  外壳与导航（见 AdminLayout 里关于「未授权显示 404」的说明）。
*/
const AdminLayout = lazy(() =>
  import('@/features/admin/AdminLayout').then((module) => ({ default: module.AdminLayout })),
)
const AdminDashboardPage = lazy(() =>
  import('@/features/admin/AdminDashboardPage').then((module) => ({
    default: module.AdminDashboardPage,
  })),
)
const AdminPostsPage = lazy(() =>
  import('@/features/admin/AdminPostsPage').then((module) => ({ default: module.AdminPostsPage })),
)
const AdminPostEditPage = lazy(() =>
  import('@/features/admin/AdminPostEditPage').then((module) => ({
    default: module.AdminPostEditPage,
  })),
)
const AdminCatalogPage = lazy(() =>
  import('@/features/admin/AdminCatalogPage').then((module) => ({
    default: module.AdminCatalogPage,
  })),
)
const AdminUsersPage = lazy(() =>
  import('@/features/admin/AdminUsersPage').then((module) => ({ default: module.AdminUsersPage })),
)
const AdminAuditPage = lazy(() =>
  import('@/features/admin/AdminAuditPage').then((module) => ({ default: module.AdminAuditPage })),
)
const AdminOpsPage = lazy(() =>
  import('@/features/admin/AdminOpsPage').then((module) => ({ default: module.AdminOpsPage })),
)

// 列表型页面共享一个模块，一次加载三个路由都有了。
const RankingPage = lazy(() =>
  import('@/features/listing/ListingPage').then((module) => ({ default: module.RankingPage })),
)
const TagPage = lazy(() =>
  import('@/features/listing/ListingPage').then((module) => ({ default: module.TagPage })),
)
const CreatorPage = lazy(() =>
  import('@/features/listing/ListingPage').then((module) => ({ default: module.CreatorPage })),
)
// 标签大全单独一个 chunk：它与上面三个页面不共享外壳，也不是同一批访问者
// （那三个是从列表/详情点进来的，这个是顶栏入口）。
const TagCatalogPage = lazy(() =>
  import('@/features/listing/TagCatalogPage').then((module) => ({
    default: module.TagCatalogPage,
  })),
)

// RequirePermission 不走懒加载：路由表在模块顶层就构造 element 的 JSX，
// 它必须同步可用。单独一个小文件正是为了不把整个 AdminLayout 拉进主 bundle，
// 见 features/admin/RequirePermission.tsx 的注释。
import { RequirePermission } from '@/features/admin/RequirePermission'
import { HomePage } from '@/features/home/HomePage'
import { NotFoundPage } from '@/features/errors/NotFoundPage'
import { PostDetailPage } from '@/features/post/PostDetailPage'

export function App() {
  // QueryClient 只能创建一次。放在 useState 的初始化里而不是模块顶层，
  // 是为了让每个 App 实例（测试里会有多个）拿到自己的缓存，互不污染。
  const [queryClient] = useState(createQueryClient)

  return (
    <RootErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          {/*
            basename 跟着构建期的 base 走（vite.config.ts 的 MODSITE_BASE_PATH）。
            部署在域名根路径时两者都是 '/'，什么都不变；部署到子路径时若只改了
            base 而漏了这里，表现是资源能加载、页面却匹配不到任何路由。
            BASE_URL 带尾斜杠，而 basename 不需要，所以去掉。
          */}
          <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/+$/, '') || '/'}>
            <AuthProvider>
              <Suspense fallback={<LoadingState className="py-24" />}>
                <Routes>
                  {/* 管理后台在 AppShell 之外：它有自己的外壳，不要标签导航与页脚。 */}
                  <Route path="admin" element={<AdminLayout />}>
                    {/*
                      每个子路由套一层 RequirePermission，理由与后端逐路由挂中间件
                      相同：改成「进了后台就都能进」再让各页面自己判断，等于把七处
                      判断散到七个文件里，漏一处的表现是页面正常打开、然后接口全部
                      403、界面只剩一片红条。概览页不套 —— 它自己按权限决定显示
                      哪几张卡片。
                    */}
                    <Route index element={<AdminDashboardPage />} />
                    <Route
                      path="posts"
                      element={
                        <RequirePermission need="content">
                          <AdminPostsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="posts/:id"
                      element={
                        <RequirePermission need="content">
                          <AdminPostEditPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="catalog"
                      element={
                        <RequirePermission need="catalog">
                          <AdminCatalogPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <RequirePermission need="users">
                          <AdminUsersPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="audit"
                      element={
                        <RequirePermission need="super">
                          <AdminAuditPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="ops"
                      element={
                        <RequirePermission need="ops">
                          <AdminOpsPage />
                        </RequirePermission>
                      }
                    />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  <Route element={<AppShell />}>
                    <Route index element={<HomePage />} />
                    <Route path="search" element={<SearchPage />} />
                    <Route path="ranking" element={<RankingPage />} />
                    {/* 标签大全。落地页仍是 /t/:publicId，这里只是那张网格。 */}
                    <Route path="tags" element={<TagCatalogPage />} />
                    {/* 标签落地页。`:publicId` 是后端标签的 public_id，
                        由 `GET /tags` 给出 —— 前端不再自造分类 id。 */}
                    <Route path="t/:publicId" element={<TagPage />} />
                    <Route path="creators/:publicId" element={<CreatorPage />} />
                    <Route path="mods/:slug" element={<PostDetailPage />} />
                    <Route path="mods/:slug/download" element={<DownloadPage />} />
                    <Route path="favorites" element={<FavoritesPage />} />
                    <Route path="publish">
                      {/* /publish 本身不是页面，默认落到整合包发布。 */}
                      <Route index element={<Navigate to="/publish/resource" replace />} />
                      <Route path="resource" element={<PublishPage kind="resource" />} />
                      <Route path="tutorial" element={<PublishPage kind="tutorial" />} />
                    </Route>
                    <Route path="login" element={<LoginPage />} />
                    <Route path="register" element={<RegisterPage />} />
                    {/* 邮件链接的落地页。前三个路径是后端契约，见上面的说明。 */}
                    <Route path="verify-email" element={<VerifyEmailPage />} />
                    <Route path="reset-password" element={<ResetPasswordPage />} />
                    <Route path="confirm-email" element={<ConfirmEmailPage />} />
                    <Route path="forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="settings/account" element={<AccountPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
              </Suspense>
              <Toaster />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </RootErrorBoundary>
  )
}
